import * as yup from "yup";
import {
  AtimInterface,
  DataModelVersion,
  HitchType,
  PtoType,
} from "protos/atk/enums";
import { getInputGuid } from "portal/utils/ids";
import {
  ImplementArchetype,
  PtoRequirements,
  ScvInputs,
} from "protos/atk/autotractor";
import {
  implementArchetypeBoundaryViewModel,
  ImplementBoundaryViewModel,
  persistImplementBoundaryViewModel,
} from "../boundaryModel";
import {
  polygon2DSchema,
  ptoRequirementsSchema,
  scvInputsSchema,
} from "../../schema";
import {
  turningRadiusLimitsToViewModel,
  TurningRadiusLimitsViewModel,
} from "portal/utils/atkEquipment";

export const EMPTY_PTO_REQUIREMENTS = {
  type: PtoType.PTO_TYPE_NONE,
};
export interface ImplementArchetypeViewModel
  extends Omit<
    ImplementArchetype,
    "turningRadiusLimits" | "boundaryModel" | "ptoRequirements" | "scvInputs"
  > {
  turningRadiusLimits: TurningRadiusLimitsViewModel;
  boundaryViewModel: ImplementBoundaryViewModel;
  ptoRequirements: PtoRequirements;
  scvInputs: ScvInputs;
}

export const viewModelSchema = yup.object({
  // basic info
  make: yup.string().required("utils.form.required"),
  model: yup.string().required("utils.form.required"),
  series: yup.string().optional(),
  hitchType: yup
    .number()
    .oneOf(
      [
        HitchType.HITCH_TYPE_DRAW_BAR_REAR,
        HitchType.HITCH_TYPE_THREE_POINT_FRONT,
        HitchType.HITCH_TYPE_THREE_POINT_REAR,
      ],
      "utils.form.required"
    )
    .required("utils.form.required"),
  atim: yup
    .number()
    .oneOf([
      AtimInterface.ATIM_GENERIC,
      AtimInterface.ATIM_LASER_WEEDER,
      AtimInterface.ATIM_NONE,
    ])
    .optional(),
  // /basic info

  // autonomy measurements
  workingWidthM: yup
    .number()
    .min(0, "utils.form.greaterOrEqualZero")
    .required("utils.form.required"),
  workingStartOffsetM: yup
    .number()
    .min(0, "utils.form.greaterOrEqualZero")
    .required("utils.form.required"),
  workingEndOffsetM: yup
    .number()
    .min(0, "utils.form.greaterOrEqualZero")
    .required("utils.form.required"),
  toolSpeedMS: yup
    .number()
    .min(0, "utils.form.greaterOrEqualZero")
    .required("utils.form.required"),
  transitionSpeedMS: yup
    .number()
    .min(0, "utils.form.greaterOrEqualZero")
    .required("utils.form.required"),
  runUpDistanceM: yup
    .number()
    .min(0, "utils.form.greaterOrEqualZero")
    .required("utils.form.required"),
  straightenDistanceM: yup
    .number()
    .min(0, "utils.form.greaterOrEqualZero")
    .required("utils.form.required"),
  boundary: polygon2DSchema.optional(),
  // /autonomy measurements

  // pto
  ptoRequirements: ptoRequirementsSchema.required("utils.form.required"),
  // /pto

  // scv
  scvInputs: scvInputsSchema.required("utils.form.required"),
  // /scv

  // turning radius
  turningRadiusLimits: yup.object<TurningRadiusLimitsViewModel>({
    radius75to100M: yup.number().positive(),
    radius50to75M: yup.number().min(yup.ref("radius75to100M")),
    radius25to50M: yup.number().min(yup.ref("radius50to75M")),
    radius0to25M: yup.number().min(yup.ref("radius25to50M")),
  }),
});

/**
 * this isn't used anywhere -- it just ensures that the schema and the
 * shape of the type it describes don't drift apart at typecheck time.
 */
implementArchetypeToViewModel(undefined) satisfies yup.InferType<
  typeof viewModelSchema
>;

export function implementArchetypeToViewModel(
  archetype: ImplementArchetype | undefined
): ImplementArchetypeViewModel {
  const baseArchetype = archetype ?? ImplementArchetype.fromPartial({});

  const turningRadiusViewModel = turningRadiusLimitsToViewModel(
    baseArchetype.turningRadiusLimits
  );

  return {
    ...baseArchetype,
    ptoRequirements:
      baseArchetype.ptoRequirements ??
      PtoRequirements.fromPartial(EMPTY_PTO_REQUIREMENTS),
    scvInputs: baseArchetype.scvInputs ?? { inputs: [] },
    turningRadiusLimits: turningRadiusViewModel,
    boundaryViewModel: implementArchetypeBoundaryViewModel(baseArchetype),
  };
}

export function viewModelToImplementArchetype(
  viewModel: ImplementArchetypeViewModel
): ImplementArchetype {
  // Adds GUIDs to scvInputs if they were missing; this fixes a bug with old
  // broken implement archetype definitions.
  const scvInputs = {
    inputs: viewModel.scvInputs.inputs.map((i) => {
      const newInput = { ...i };
      if (!i.id) {
        newInput.id = getInputGuid();
      }
      return i;
    }),
  };

  return {
    ...viewModel,
    ...persistImplementBoundaryViewModel(
      viewModel.boundaryViewModel,
      viewModel.boundary,
      // FIXME: this needs to be made asymmetrical-implement safe
      viewModel.workingWidthM / 2,
      viewModel.workingWidthM / 2,
      viewModel.workingStartOffsetM
    ),
    scvInputs,
    turningRadiusLimits: [
      {
        maxHeight: 25,
        minTurningRadiusM: viewModel.turningRadiusLimits.radius0to25M,
      },
      {
        maxHeight: 50,
        minTurningRadiusM: viewModel.turningRadiusLimits.radius25to50M,
      },
      {
        maxHeight: 75,
        minTurningRadiusM: viewModel.turningRadiusLimits.radius50to75M,
      },
      {
        maxHeight: 100,
        minTurningRadiusM: viewModel.turningRadiusLimits.radius75to100M,
      },
    ],
  };
}

export function editingSupported(
  viewModel: ImplementArchetypeViewModel
): boolean {
  return (
    viewModel.dataModelVersion < DataModelVersion.DATA_MODEL_VERSION_M6_BASE
  );
}
